import Icon from '@/components/ui/AppIcon';

interface CycleStep {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: string;
}

export default function ServiceCycleInfographic() {
  const cycleSteps: CycleStep[] = [
    {
      id: 'step_1',
      number: '01',
      title: 'Onboarding',
      description: 'We start by understanding your brand, goals, and target audience to create a solid foundation.',
      icon: 'UserPlusIcon',
    },
    {
      id: 'step_2',
      number: '02',
      title: 'Understanding Background',
      description: 'Deep dive into your industry, competitors, and market positioning to craft winning strategies.',
      icon: 'MagnifyingGlassIcon',
    },
    {
      id: 'step_3',
      number: '03',
      title: 'Content Calendar',
      description: 'Strategic planning of posts, reels, stories, and campaigns for the entire month ahead.',
      icon: 'CalendarDaysIcon',
    },
    {
      id: 'step_4',
      number: '04',
      title: 'Profile Handling',
      description: 'Daily management of your social media profiles, engagement, and community building.',
      icon: 'DevicePhoneMobileIcon',
    },
    {
      id: 'step_5',
      number: '05',
      title: 'Ads & Analytics',
      description: 'Running targeted paid campaigns and tracking performance metrics for continuous optimization.',
      icon: 'ChartBarIcon',
    },
    {
      id: 'step_6',
      number: '06',
      title: 'Leads & Acquisition',
      description: 'Converting engagement into qualified leads and customers through strategic nurturing.',
      icon: 'UserGroupIcon',
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-zinc-950 border-y border-zinc-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-[2px] h-4 bg-gradient-to-b from-pink-500 to-purple-600" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
              Monthly Service Cycle
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Complete Monthly Workflow
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            A proven 6-step process that repeats every month, ensuring consistent growth and engagement.
          </p>
        </div>

        {/* Cycle Visualization */}
        <div className="relative max-w-6xl mx-auto">
          {/* Center Circle - Repeat Indicator */}
          <div className="hidden lg:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 items-center justify-center z-10">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full blur-xl opacity-30 animate-pulse" />
              <div className="relative bg-zinc-900 border-2 border-pink-500 rounded-full w-48 h-48 flex flex-col items-center justify-center">
                <Icon name="ArrowPathIcon" size={48} className="text-pink-500 mb-2" />
                <span className="text-white font-bold text-lg">Repeats</span>
                <span className="text-zinc-400 text-sm">Every Month</span>
              </div>
            </div>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cycleSteps.map((step, idx) => (
              <div
                key={step.id}
                className="relative reveal"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                {/* Connector Arrow - visible on larger screens */}
                {idx < cycleSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 left-full w-8 h-[2px] bg-gradient-to-r from-pink-500 to-purple-600 -translate-y-1/2 z-0">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-l-8 border-l-purple-600 border-t-4 border-t-transparent border-b-4 border-b-transparent" />
                  </div>
                )}

                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-pink-500/30 transition-all glow-hover h-full relative overflow-hidden group">
                  {/* Background Gradient Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="relative z-10">
                    <div className="text-6xl font-bold text-zinc-800 mb-4">
                      {step.number}
                    </div>
                    <div className="w-12 h-12 mb-4 flex items-center justify-center text-pink-500 bg-pink-500/10 rounded-xl">
                      <Icon name={step.icon as any} size={24} />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-white">{step.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Repeat Indicator */}
          <div className="lg:hidden mt-12 flex justify-center reveal">
            <div className="bg-zinc-900 border-2 border-pink-500 rounded-2xl px-8 py-6 flex items-center gap-4">
              <Icon name="ArrowPathIcon" size={32} className="text-pink-500" />
              <div>
                <span className="text-white font-bold text-lg block">Repeats Every Month</span>
                <span className="text-zinc-400 text-sm">Continuous growth cycle</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}